/**
 * Calendar + directions link builders shared by the RSVP page and the
 * invitation email. Framework-free (no browser or Node APIs) so the same
 * logic renders links on the public page and generates the `.ics` file
 * served by the backend — never duplicated per surface.
 */
/** `YYYYMMDD` for a date-only value, read as the calendar day it is (UTC). */
function icsDay(value, addDays = 0) {
    // `new Date('YYYY-MM-DD')` is UTC midnight, the same as a stored date-only value.
    const day = toDate(value);
    const shifted = new Date(Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate() + addDays));
    return shifted.toISOString().slice(0, 10).replace(/-/g, '');
}
/** Default event length when no end time is known (3 hours). */
const DEFAULT_DURATION_MS = 3 * 60 * 60 * 1000;
function toDate(value) {
    return value instanceof Date ? value : new Date(value);
}
function resolveRange(evt) {
    const start = toDate(evt.start);
    const end = evt.end != null && String(evt.end).length > 0
        ? toDate(evt.end)
        : new Date(start.getTime() + DEFAULT_DURATION_MS);
    // Guard against an end that is not after the start (bad data) — fall back
    // to the default duration so calendar apps don't reject the entry.
    if (end.getTime() <= start.getTime()) {
        return { start, end: new Date(start.getTime() + DEFAULT_DURATION_MS) };
    }
    return { start, end };
}
/** UTC basic format `YYYYMMDDTHHMMSSZ` used by every calendar target. */
export function toCalendarUtc(value) {
    const date = toDate(value);
    return date
        .toISOString()
        .replace(/[-:]/g, '')
        .replace(/\.\d{3}/, '');
}
/** Google Calendar "create event" URL. */
export function googleCalendarUrl(evt) {
    const { start, end } = resolveRange(evt);
    const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: evt.title,
        dates: `${toCalendarUtc(start)}/${toCalendarUtc(end)}`,
    });
    const details = [evt.description, evt.url].filter(Boolean).join('\n\n');
    if (details)
        params.set('details', details);
    if (evt.location)
        params.set('location', evt.location);
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
/** Outlook (outlook.live.com) compose-event deep link. */
export function outlookCalendarUrl(evt) {
    const { start, end } = resolveRange(evt);
    const params = new URLSearchParams({
        path: '/calendar/action/compose',
        rru: 'addevent',
        subject: evt.title,
        startdt: toDate(start).toISOString(),
        enddt: toDate(end).toISOString(),
    });
    const body = [evt.description, evt.url].filter(Boolean).join('\n\n');
    if (body)
        params.set('body', body);
    if (evt.location)
        params.set('location', evt.location);
    return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}
/** Yahoo Calendar add-event URL (uses local-basic timestamps). */
export function yahooCalendarUrl(evt) {
    const { start, end } = resolveRange(evt);
    const params = new URLSearchParams({
        v: '60',
        title: evt.title,
        st: toCalendarUtc(start),
        et: toCalendarUtc(end),
    });
    const desc = [evt.description, evt.url].filter(Boolean).join(' ');
    if (desc)
        params.set('desc', desc);
    if (evt.location)
        params.set('in_loc', evt.location);
    return `https://calendar.yahoo.com/?${params.toString()}`;
}
/** Google Maps directions link to the venue address (or name). */
export function directionsUrl(destination) {
    if (!destination || !destination.trim())
        return null;
    const params = new URLSearchParams({ api: '1', destination: destination.trim() });
    return `https://www.google.com/maps/dir/?${params.toString()}`;
}
/** Escapes a value per RFC 5545 (commas, semicolons, backslashes, newlines). */
function escapeIcs(value) {
    return value
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\r?\n/g, '\\n');
}
function icsUid(evt, index) {
    if (evt.uid)
        return evt.uid;
    const stamp = toCalendarUtc(toDate(evt.start));
    const slug = evt.title.replace(/[^A-Za-z0-9]/g, '').slice(0, 24) || 'event';
    return `${stamp}-${index}-${slug}@np-productions.com`;
}
function buildVevent(evt, index, dtStamp) {
    const when = evt.allDay
        ? [`DTSTART;VALUE=DATE:${icsDay(evt.start)}`, `DTEND;VALUE=DATE:${icsDay(evt.start, 1)}`]
        : (() => {
            const { start, end } = resolveRange(evt);
            return [`DTSTART:${toCalendarUtc(start)}`, `DTEND:${toCalendarUtc(end)}`];
        })();
    const lines = [
        'BEGIN:VEVENT',
        `UID:${icsUid(evt, index)}`,
        `DTSTAMP:${dtStamp}`,
        ...when,
        `SUMMARY:${escapeIcs(evt.title)}`,
    ];
    const description = [evt.description, evt.url].filter(Boolean).join('\n\n');
    if (description)
        lines.push(`DESCRIPTION:${escapeIcs(description)}`);
    if (evt.location)
        lines.push(`LOCATION:${escapeIcs(evt.location)}`);
    lines.push('END:VEVENT');
    return lines;
}
export function buildIcs(events, dtStamp, options = {}) {
    const list = Array.isArray(events) ? events : [events];
    const stamp = toCalendarUtc(dtStamp ?? toDate(list[0]?.start ?? new Date(0)));
    const refresh = options.refreshMinutes;
    const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//NP Events & Production//RSVP//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        ...(options.calendarName ? [`X-WR-CALNAME:${escapeIcs(options.calendarName)}`] : []),
        ...(refresh && refresh > 0
            ? [`REFRESH-INTERVAL;VALUE=DURATION:PT${refresh}M`, `X-PUBLISHED-TTL:PT${refresh}M`]
            : []),
        ...list.flatMap((evt, index) => buildVevent(evt, index, stamp)),
        'END:VCALENDAR',
    ];
    // RFC 5545 requires CRLF line endings.
    return lines.join('\r\n');
}
//# sourceMappingURL=calendarLinks.js.map